// src/components/common/Header.tsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  Badge,
  Grid,
  useTheme,
  useMediaQuery,

} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useClerk } from "@clerk/clerk-react";
import { useSidebar } from "../../contexts/SidebarContext";
import { useTutorialOptional } from "../tutorial/TutorialContext";

/** Worldlink Brand Tokens */
const WL_RED = "#EE2D3D";
const WL_RED_DARK = "#D42534";
const WL_BLACK = "#111111";
const WHITE_BG = "#FFFFFF";
const RED = "#EE2D3D";
const RED_DARK = "#D42534";
const TEXT_BLACK = "#111111";
const TEXT_MUTED = "#6B7280";

/** Video metadata */
type VideoKey = "overview" | "tariff" | "risk" | "route";

interface VideoData {
  title: string;
  duration: string;
  description: string;
  order: number;
  cardDescription: string;
  gradient: string;
}

const videos: Record<VideoKey, VideoData> = {
  overview: {
    title: "Platform Overview",
    duration: "~3 min",
    description:
      "Get a comprehensive tour of the WorldLink Supply Chain Intelligence Platform. See how our three core modules—Tariff Intelligence, Risk Prediction, and Route Optimization—work together to provide complete visibility across your global supply chain.",
    order: 0,
    cardDescription: "Explore all three modules and see how they integrate.",
    gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 70%, #f87171 100%)",
  },
  tariff: {
    title: "Tariff Intelligence",
    duration: "~2 min",
    description:
      "Learn how the AI Assistant and Tariff Cost Calculator work together to provide precise landed-cost estimates. Enter HTS codes, origin country, and shipment value to instantly see base duties, additional charges, and effective duty rates.",
    order: 1,
    cardDescription:
      "See how we estimate duties and landed cost using HTS codes and country inputs.",
    gradient: "linear-gradient(135deg, #c1121f 0%, #e63946 70%, #ff4d5c 100%)",
  },
  risk: {
    title: "Risk Prediction",
    duration: "~1.5 min",
    description:
      "Discover how our machine learning model analyzes thousands of historical shipments to predict delay probability. See how risk scores, color-coded warnings, and delay factors help your team take proactive action.",
    order: 2,
    cardDescription:
      "Discover how shipment delay risk is scored and visualized from your historical data.",
    gradient: "linear-gradient(135deg, #b81c2e 0%, #d62639 70%, #e63946 100%)",
  },
  route: {
    title: "Route Optimization",
    duration: "~1.5 min",
    description:
      "Watch the graph-based routing engine in action. Learn how it evaluates distance, transit time, cost, and risk to find the best route between any two locations on the global network.",
    order: 3,
    cardDescription:
      "Watch how the graph-based engine finds the best route between any two locations.",
    gradient: "linear-gradient(135deg, #8b1520 0%, #a91b27 70%, #c1121f 100%)",
  },
};

const videoOrder: VideoKey[] = ["overview", "tariff", "risk", "route"];

/** DemoHubPopup Component */
interface DemoHubPopupProps {
  open: boolean;
  onClose: () => void;
}

const DemoHubPopup: React.FC<DemoHubPopupProps> = ({ open, onClose }) => {
  const [view, setView] = useState<"hub" | "player">("hub");
  const [selectedVideo, setSelectedVideo] = useState<VideoKey>("overview");
  const [hoveredCard, setHoveredCard] = useState<VideoKey | null>(null);

  const handleCardClick = (videoKey: VideoKey) => {
    setSelectedVideo(videoKey);
    setView("player");
  };

  const handleBack = () => {
    setView("hub");
  };

  const handleClose = () => {
    setView("hub");
    setSelectedVideo("overview");
    onClose();
  };

  const currentIndex = videoOrder.indexOf(selectedVideo);
  const currentVideo = videos[selectedVideo];

  const handlePrevious = () => {
    if (currentIndex > 0) setSelectedVideo(videoOrder[currentIndex - 1]);
  };

  const handleNext = () => {
    if (currentIndex < videoOrder.length - 1) {
      setSelectedVideo(videoOrder[currentIndex + 1]);
    }
  };

  if (!open) return null;

  return (
    <Box
      onClick={handleClose}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: "rgba(0, 0, 0, 0.15)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: 900,
          maxHeight: "90vh",
          backgroundColor: WHITE_BG,
          borderRadius: 3,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mx: 2,
        }}
      >
        {/* Sticky Top Bar */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: WHITE_BG,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              color: TEXT_BLACK,
              fontFamily: `"Plus Jakarta Sans", sans-serif`,
            }}
          >
            Demo Videos
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: TEXT_BLACK,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 3 }}>
          {view === "hub" ? (
            <Grid container spacing={2}>
              {videoOrder.map((videoKey) => {
                const video = videos[videoKey];
                const isHovered = hoveredCard === videoKey;
                const isFeatured = videoKey !== "overview";

                return (
                  <Grid size={{ xs: 6, sm: 6, md: 6 }} key={videoKey}>
                    <Box
                      onClick={() => handleCardClick(videoKey)}
                      onMouseEnter={() => setHoveredCard(videoKey)}
                      onMouseLeave={() => setHoveredCard(null)}
                      sx={{
                        height: 200,
                        borderRadius: 2,
                        background: video.gradient,
                        position: "relative",
                        cursor: "pointer",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        p: 2.5,
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                        },
                      }}
                    >
                      {isFeatured && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            backgroundColor: WHITE_BG,
                            color: RED,
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: `"Plus Jakarta Sans", sans-serif`,
                          }}
                        >
                          Featured
                        </Box>
                      )}

                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: WHITE_BG,
                            textAlign: "center",
                            mb: 2,
                            fontFamily: `"Plus Jakarta Sans", sans-serif`,
                          }}
                        >
                          {video.title}
                        </Typography>

                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            backgroundColor: isHovered ? WHITE_BG : RED,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                            transform: isHovered ? "scale(1.1)" : "scale(1)",
                          }}
                        >
                          <PlayArrowIcon
                            sx={{
                              fontSize: 28,
                              color: isHovered ? RED : WHITE_BG,
                              transition: "color 0.2s ease",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.2)",
                          mx: -2.5,
                          mb: -2.5,
                          mt: 2,
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: WHITE_BG,
                            fontFamily: `"Plus Jakarta Sans", sans-serif`,
                            lineHeight: 1.4,
                          }}
                        >
                          {video.cardDescription}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Button
                  onClick={handleBack}
                  sx={{
                    color: RED,
                    fontWeight: 600,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "rgba(238,45,61,0.04)" },
                  }}
                >
                  &larr; Back
                </Button>

                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: TEXT_BLACK,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                  }}
                >
                  {currentVideo.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#6B7280",
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                  }}
                >
                  {currentVideo.duration}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  paddingTop: "56.25%",
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(135deg, #1F2937 0%, #374151 100%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 48, color: WHITE_BG }} />
                  </Box>
                  <Typography
                    sx={{
                      color: WHITE_BG,
                      fontSize: 18,
                      fontFamily: `"Plus Jakarta Sans", sans-serif`,
                    }}
                  >
                    {currentVideo.title} Video Player
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: WHITE_BG,
                  border: "1px solid #E5E7EB",
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: TEXT_BLACK,
                    mb: 1.5,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                  }}
                >
                  About this module
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: TEXT_MUTED,
                    lineHeight: 1.6,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                  }}
                >
                  {currentVideo.description}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  variant="outlined"
                  sx={{
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                    textTransform: "none",
                    borderColor: "#D1D5DB",
                    color: currentIndex === 0 ? "#9CA3AF" : TEXT_BLACK,
                    "&:hover": {
                      borderColor: currentIndex === 0 ? "#D1D5DB" : RED_DARK,
                      backgroundColor:
                        currentIndex === 0 ? "transparent" : "rgba(238,45,61,0.04)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#E5E7EB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  &larr; Previous
                </Button>

                <Button
                  onClick={handleNext}
                  disabled={currentIndex === videoOrder.length - 1}
                  variant="contained"
                  sx={{
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontFamily: `"Plus Jakarta Sans", sans-serif`,
                    textTransform: "none",
                    backgroundColor:
                      currentIndex === videoOrder.length - 1 ? "#9CA3AF" : RED,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        currentIndex === videoOrder.length - 1 ? "#9CA3AF" : RED_DARK,
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#E5E7EB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  Next &rarr;
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { toggleSidebar } = useSidebar();
  const { openUserProfile, signOut } = useClerk();
  const tutorialCtx = useTutorialOptional();
  const steps = tutorialCtx?.steps ?? [];
  const startTutorial = tutorialCtx?.startTutorial ?? (() => undefined);
  const hasTutorial = steps.length > 0;

  const [demoHubOpen, setDemoHubOpen] = useState(false);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  
  const isLandingPage = location.pathname === "/home";

  const handleLogoClick = () => {
    navigate("/home");
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 56,
        minHeight: 56,
        flexShrink: 0,
        backgroundColor: WHITE_BG,
        borderBottom: "1px solid #E5E7EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        zIndex: 1000,
      }}
    >
      {/* Left: Hamburger Menu (Mobile) + Logo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {/* Hamburger Menu Button - Only on Mobile, not on landing page */}
        {isMobile && !isLandingPage && (
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              color: WL_BLACK,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {/* Logo */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src="/worldlinklogo.svg"
            alt="WorldLink"
            sx={{
              height: "22px",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Right: Search, Notifications, User Profile */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "28px" }}>

        {/* Learn More and Watch Demo buttons (only on landing page) */}
        {isLandingPage && (
          <>
            <Button
              variant="contained"
              sx={{
                backgroundColor: WL_RED,
                color: WHITE_BG,
                borderRadius: 2,
                px: 2.5,
                py: 0.8,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.9rem",
                "&:hover": { backgroundColor: WL_RED_DARK },
              }}
              onClick={() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
              }}
            >
              Learn More
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: WL_RED,
                color: WL_RED,
                borderRadius: 2,
                px: 2.5,
                py: 0.8,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.9rem",
                "&:hover": {
                  borderColor: WL_RED_DARK,
                  backgroundColor: "rgba(238,45,61,0.06)",
                },
              }}
              onClick={() => setDemoHubOpen(true)}
            >
              Watch Demo
            </Button>
          </>
        )}

        {/* Notifications Icon with red dot */}
        <IconButton
          onClick={(event: React.MouseEvent<HTMLElement>) => setNotificationAnchor(event.currentTarget)}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          <Badge
            variant="dot"
            invisible={true}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: WL_RED,
                minWidth: 8,
                height: 8,
                top: 2,
                right: 2,
              },
            }}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>

        {/* Page Tutorial Trigger */}
        {hasTutorial && (
          <Button
            onClick={startTutorial}
            size="small"
            aria-label="start page tutorial"
            sx={{
              color: "#6B7280",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": { backgroundColor: "rgba(54, 124, 43, 0.08)", color: "#367C2B" },
            }}
          >
            Quick Tour
          </Button>
        )}

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={() => setNotificationAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 280,
              maxWidth: 320,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
            <Typography sx={{ fontWeight: 600, color: TEXT_BLACK, fontSize: "0.95rem" }}>
              Notifications
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography sx={{ color: TEXT_MUTED, fontSize: "0.875rem" }}>
              alerts will appear here
            </Typography>
          </Box>
        </Menu>

        {/* User Profile Icon with Menu */}
        <IconButton
          onClick={(event: React.MouseEvent<HTMLElement>) => setProfileAnchor(event.currentTarget)}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          <PersonOutlineOutlinedIcon sx={{ fontSize: 24 }} />
        </IconButton>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={() => setProfileAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 180,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: 2,
            },
          }}
        >
          <Box
            sx={{ px: 2, py: 1.5, cursor: "pointer", "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}
            onClick={() => { setProfileAnchor(null); openUserProfile(); }}
          >
            <Typography sx={{ fontWeight: 500, color: TEXT_BLACK, fontSize: "0.9rem" }}>
              Manage Account
            </Typography>
          </Box>
          <Box
            sx={{ px: 2, py: 1.5, cursor: "pointer", "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}
            onClick={() => { setProfileAnchor(null); signOut({ redirectUrl: "/login" }); }}
          >
            <Typography sx={{ fontWeight: 500, color: WL_RED, fontSize: "0.9rem" }}>
              Sign Out
            </Typography>
          </Box>
        </Menu>
      </Box>

      {/* Demo Hub Popup */}
      <DemoHubPopup open={demoHubOpen} onClose={() => setDemoHubOpen(false)} />
    </Box>
  );
};

export default Header;
